(ns practitest.frontend.dashboard-item-settings.events
  (:require [ajax.core :as ajax]
            [re-frame.core :as re-frame]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.components2.dashboard.events :as dashboard-events]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.subs :as global-subs]))

(def db-key :dashboard-item-settings)

(def empty-state
  {:item             {}
   :dashboards       []
   :item-types       []
   :entity-fields    []
   :relevant-fields  []
   :field-pvs        {}
   :instance-fields  []
   :advanced-options {}
   :loading?         false
   :saving?          false
   :saved-item-id    nil
   :embed-code       nil
   :data-fetch-url   nil
   :errors           {}
   :dirty?           false})

(def item-type->load-event dashboard-events/item-type->load-event)

(re-frame/reg-fx
 ::navigate!
 (fn [url]
   (set! (.-href js/window.location) url)))

(re-frame/reg-fx
 ::navigate-back!
 (fn [fallback-url]
   (if (> (.-length js/window.history) 1)
     (.back js/window.history)
     (set! (.-href js/window.location) fallback-url))))

(re-frame/reg-event-fx
 ::init
 (fn [{:keys [db]} _]
   (let [project-id    (global-subs/get-current-project-id db)
         item-id       (get-in db [:current-route :path-params :item-id])
         dashboard-id  (some-> (get-in db [:current-route :query-params :dashboard_id]) js/parseInt)]
     {:db (assoc db db-key (assoc empty-state :loading? true))
      :dispatch [::fetch-form-options {:project-id project-id
                                       :item-id item-id
                                       :dashboard-id dashboard-id}]})))

(re-frame/reg-event-db
 ::cleanup
 (fn [db _]
   (dissoc db db-key)))

(re-frame/reg-event-fx
 ::fetch-form-options
 (fn [{:keys [db]} [_ {:keys [project-id item-id dashboard-id]}]]
   (let [query-params (cond-> {} item-id (assoc :item_id item-id))
         uri (broutes/get-url "dashboard_items#form_options" {:p_id project-id} query-params)]
     {:http-xhrio {:method          :get
                   :uri             uri
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::on-form-options-success {:project-id project-id
                                                                :item-id    item-id
                                                                :dashboard-id dashboard-id}]
                   :on-failure      [::on-fetch-failure]}})))

(re-frame/reg-event-fx
 ::on-form-options-success
 (fn [{:keys [db]} [_ {:keys [dashboard-id]} response]]
   (let [item      (or (:item response) (when dashboard-id {:dashboard_id dashboard-id}))
         fetch-url (:data_fetch_url item)
         load-evt  (when fetch-url (get item-type->load-event (:item_type item)))
         entity    (:entity item)
         item-type (:item_type item)
         dispatches (cond-> []
                      (and load-evt fetch-url) (conj [load-evt fetch-url])
                      entity                   (conj [::fetch-entity-fields {:entity entity :item-type item-type}]))]
     (cond-> {:db (update db db-key assoc
                          :dashboards      (:dashboards response)
                          :item-types      (:item_types response)
                          :item            (or item {})
                          :saved-item-id   (:id item)
                          :embed-code      (:embed_code item)
                          :data-fetch-url  fetch-url
                          :relevant-fields (or (:relevant_fields response) [])
                          :loading?        false)}
       (seq dispatches) (assoc :dispatch-n dispatches)))))

(re-frame/reg-event-fx
 ::fetch-entity-fields
 (fn [{:keys [db]} [_ {:keys [entity item-type]}]]
   (let [project-id (global-subs/get-current-project-id db)
         query      (cond-> {:entity entity}
                      item-type (assoc :item_type item-type))
         uri (broutes/get-url "dashboard_items#entity_fields" {:p_id project-id} query)]
     {:http-xhrio {:method          :get
                   :uri             uri
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::on-entity-fields-success]
                   :on-failure      [::on-fetch-failure]}})))

(defn- merge-response-fields [db response]
  (update db db-key assoc
          :entity-fields    (or (:entity_fields response) [])
          :relevant-fields  (or (:relevant_fields response) [])
          :field-pvs        (or (:field_pvs response) {})
          :instance-fields  (or (:instance_fields response) [])
          :advanced-options (or (:advanced_options response) {})))

(defn- autoselect-single-x-field [db]
  (let [entity-fields (get-in db [db-key :entity-fields])]
    (cond-> db
      (and (= 1 (count entity-fields))
           (nil? (get-in db [db-key :item :x_field_id])))
      (assoc-in [db-key :item :x_field_id] (:value (first entity-fields))))))

(re-frame/reg-event-db
 ::on-entity-fields-success
 (fn [db [_ response]]
   (-> db
       (merge-response-fields response)
       autoselect-single-x-field)))

(re-frame/reg-event-fx
 ::on-fetch-failure
 (fn [{:keys [db]} [_ _response]]
   {:db       (assoc-in db [db-key :loading?] false)
    :dispatch [::notifications/error2
               (get-translation "dashboard-item.notify.load-failed" "Failed to load dashboard item data")]}))

(re-frame/reg-event-db
 ::update-field
 (fn [db [_ field value]]
   (-> db
       (assoc-in [db-key :item field] value)
       (assoc-in [db-key :dirty?] true))))

(re-frame/reg-event-db
 ::update-x-field
 (fn [db [_ value]]
   (-> db
       (update-in [db-key :item] assoc
                  :x_field_id       value
                  :x_field_criteria nil)
       (assoc-in [db-key :dirty?] true))))

(re-frame/reg-event-db
 ::update-run-field-filter
 (fn [db [_ value]]
   (-> db
       (update-in [db-key :item] assoc
                  :run_field_filter_id       value
                  :run_field_filter_criteria nil)
       (assoc-in [db-key :dirty?] true))))

(re-frame/reg-event-db
 ::update-instance-filter
 (fn [db [_ filter-map]]
   (-> db
       (assoc-in [db-key :item :definition :instance_filter] filter-map)
       (assoc-in [db-key :dirty?] true))))

(re-frame/reg-event-db
 ::update-definition
 (fn [db [_ field value]]
   (-> db
       (assoc-in [db-key :item :definition field] value)
       (assoc-in [db-key :dirty?] true))))

(re-frame/reg-event-fx
 ::update-entity
 (fn [{:keys [db]} [_ entity]]
   {:db       (-> db
                  (update-in [db-key :item] assoc
                             :entity                    entity
                             :item_type                 nil
                             :custom_view_id            nil
                             :custom_view_auto_filter   nil
                             :x_field_id                nil
                             :y_field_id                nil
                             :run_field_filter_id       nil
                             :run_field_filter_criteria nil
                             :definition                nil
                             :cell_width                nil
                             :cell_height               nil
                             :colors                    nil
                             :remove_empty_values       nil
                             :percentage_mode           nil)
                  (update db-key assoc
                          :entity-fields    []
                          :relevant-fields  []
                          :instance-fields  []
                          :advanced-options {}
                          :dirty?           true))
    :dispatch [::fetch-entity-fields {:entity entity}]}))

(re-frame/reg-event-fx
 ::update-item-type
 (fn [{:keys [db]} [_ item-type]]
   (let [entity (get-in db [db-key :item :entity])]
     {:db       (-> db
                    (update-in [db-key :item] assoc
                               :item_type           item-type
                               :x_field_id          nil
                               :y_field_id          nil
                               :definition          nil
                               :cell_width          nil
                               :cell_height         nil
                               :colors              nil
                               :remove_empty_values nil
                               :percentage_mode     nil)
                    (update db-key assoc
                            :relevant-fields  []
                            :advanced-options {}
                            :dirty?           true))
      :dispatch [::fetch-entity-fields {:entity entity :item-type item-type}]})))

(re-frame/reg-event-db
 ::update-custom-view
 (fn [db [_ view-id auto-filter]]
   (-> db
       (update-in [db-key :item] assoc
                  :custom_view_id          view-id
                  :custom_view_auto_filter auto-filter)
       (assoc-in [db-key :dirty?] true))))

(defn relevant-field? [relevant-fields field-name]
  (boolean (some #{field-name} relevant-fields)))

(defn- save-params [item]
  {:dashboard_item
   {:name                             (:name item)
    :entity                           (:entity item)
    :item_type                        (:item_type item)
    :custom_view_id                   (:custom_view_id item)
    :custom_view_auto_filter          (:custom_view_auto_filter item)
    :dashboard_id                     (:dashboard_id item)
    :x_field_update                   (:x_field_id item)
    :y_field_update                   (:y_field_id item)
    :x_field_criteria_update          (:x_field_criteria item)
    :run_field_filter_update          (:run_field_filter_id item)
    :run_field_filter_criteria_update (:run_field_filter_criteria item)
    :definition_update                (:definition item)
    :colors                           (:colors item)
    :cell_width                       (:cell_width item)
    :cell_height                      (:cell_height item)
    :remove_empty_values              (:remove_empty_values item)
    :percentage_mode                  (:percentage_mode item)}})

(defn- validate-item [item]
  (cond
    (nil? (:entity item))    (get-translation "dashboard-item.validate.entity-required" "Please select an Entity")
    (nil? (:item_type item)) (get-translation "dashboard-item.validate.item-type-required" "Please select an Item Type")
    :else nil))

(re-frame/reg-event-fx
 ::save
 (fn [{:keys [db]} [_ {:keys [close?]}]]
   (let [project-id (global-subs/get-current-project-id db)
         item       (get-in db [db-key :item])
         error-msg  (validate-item item)]
     (if error-msg
       {:dispatch [::notifications/error2 error-msg]}
       (let [item-id (:id item)
             edit?   (some? item-id)
             method  (if edit? :put :post)
             uri     (if edit?
                       (broutes/get-url "dashboard_items#update" {:p_id project-id
                                                                  :id item-id})
                       (broutes/get-url "dashboard_items#create" {:p_id project-id}))]
         {:db (assoc-in db [db-key :saving?] true)
          :http-xhrio {:method          method
                       :uri             uri
                       :params          (save-params item)
                       :format          (ajax/json-request-format)
                       :response-format (ajax/json-response-format {:keywords? true})
                       :on-success      [::on-save-success {:project-id project-id :close? close? :new? (not edit?)}]
                       :on-failure      [::on-save-failure]}})))))

(re-frame/reg-event-fx
 ::on-save-success
 (fn [{:keys [db]} [_ {:keys [project-id close? new?]} response]]
   (let [item-id   (:id response)
         fetch-url (:data_fetch_url response)
         load-evt  (when fetch-url (get item-type->load-event (:item_type response)))
         new-db    (-> db
                       (update db-key assoc
                               :saving?        false
                               :item           response
                               :saved-item-id  item-id
                               :embed-code     (:embed_code response)
                               :data-fetch-url fetch-url
                               :errors         {}
                               :dirty?         false)
                       (update :dashboard-items dissoc item-id))]
     (cond
       close?
       {:db new-db ::navigate-back! (str "/p/" project-id "/dashboards")}

       new?
       ;; After creating a new item, navigate to its edit URL so the URL reflects the saved item
       {:db new-db ::navigate! (str "/p/" project-id "/dashboard_items/" item-id "/edit")}

       :else
       (let [dispatches (cond-> [[::notifications/success2
                                  (get-translation "dashboard-item.notify.saved" "Dashboard item saved successfully")]]
                          (and load-evt fetch-url) (conj [load-evt fetch-url]))]
         {:db new-db :dispatch-n dispatches})))))

(re-frame/reg-event-fx
 ::on-save-failure
 (fn [{:keys [db]} [_ response]]
   {:db       (assoc-in db [db-key :saving?] false)
    :dispatch [::notifications/error2
               (or (get-in response [:response :message])
                   (get-translation "dashboard-item.notify.save-failed" "Failed to save dashboard item"))]}))

(re-frame/reg-event-fx
 ::cancel
 (fn [{:keys [db]} _]
   (let [project-id (global-subs/get-current-project-id db)]
     {::navigate-back! (str "/p/" project-id "/dashboards")})))
